/* eslint-disable @typescript-eslint/no-floating-promises */
import { useCallback, useEffect, useRef, useState } from 'react';
import enviro from 'enviro';
import styled from 'styled-components';
import PortalIdParser from 'PortalIdParser';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import GenerateButton from './GenerateButton';
import CallSummaryContent from './CallSummaryContent';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { usePollForCallSummary } from '../hooks/usePollForCallSummary';
import { generateCallSummary } from '../clients/CallSummaryAPI';
import { logNetworkError } from 'calling-error-reporting/report/error';
import { DISTANCE_MEASUREMENT_SMALL } from 'HubStyleTokens/sizes';
import { useContentAssistance } from '../hooks/useContentAssistance';
import UILink from 'UIComponents/link/UILink';
import UIAlert from 'UIComponents/alert/UIAlert';
import { loadCSATSurvey } from '../utils/feedback';
import Feedback from './Feedback';
import { useCallPropertyHistory } from '../hooks/useCallPropertyHistory';
import { useNextBestActions } from '../hooks/useNextBestActions';
import { useAuth } from '../hooks/useAuth';
import { CALLING_NEXT_BEST_ACTIONS } from 'calling-internal-common/constants/CallingGates';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const client = new DataFetchingClient();
const contentAssistanceSettingsURL = `/settings/${PortalIdParser.get()}/ai-assistants`;
const CALL_SUMMARIZATION_SURVEY_ID = 1025;
const CallSummaryWrapper = styled.div.withConfig({
  displayName: "CallSummaryWithProvider__CallSummaryWrapper",
  componentId: "sc-87kypr-0"
})(["padding-bottom:", ";margin-bottom:", ";border-bottom:1px solid ", ";"], DISTANCE_MEASUREMENT_SMALL, DISTANCE_MEASUREMENT_SMALL, BATTLESHIP);
const CallSummaryContentWrapper = styled.div.withConfig({
  displayName: "CallSummaryWithProvider__CallSummaryContentWrapper",
  componentId: "sc-87kypr-1"
})(["padding-top:", ";"], DISTANCE_MEASUREMENT_SMALL);
const CallSummary = ({
  callId,
  canEdit,
  engagementId,
  handleEngagementDataUpdated,
  initialCallSummary,
  initialNextBestActions,
  initialCallSummaryExecutionId,
  initialLastUpdatedAtTimestamp,
  initialLastUpdatedByUserId,
  isTranscriptEmpty,
  isTranscriptScheduled,
  style,
  usageTracker,
  gates,
  shouldShowNextBestActions = true
}) => {
  const [executionId, setExecutionId] = useState(initialCallSummaryExecutionId);
  const [callSummary, setCallSummary] = useState(initialCallSummary);
  const [showInitialLoading, setShowInitialLoading] = useState(false);
  const [showExtendedLoading, setShowExtendedLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const {
    customerAnalysisEnabled
  } = useContentAssistance();
  const [lastUpdatedAtTimestamp, setLastUpdatedAtTimestamp] = useState(initialLastUpdatedAtTimestamp || null);
  const [lastUpdatedByUserId, setLastUpdatedByUserId] = useState(initialLastUpdatedByUserId || null);
  const initialLoadingTimeout = 3000;
  const timeoutId = useRef(undefined);
  const isUngatedForNextBestActions = !!gates && gates.includes(CALLING_NEXT_BEST_ACTIONS);
  const resetLoadingState = () => {
    clearTimeout(timeoutId.current);
    setShowInitialLoading(false);
    setShowExtendedLoading(false);
  };
  const {
    isLoadingUserInfo,
    userInfo
  } = useAuth();
  const {
    callSummaryHistory,
    loading: isLoadingCallSummaryHistory,
    error: fetchCallPropertyHistoryError
  } = useCallPropertyHistory({
    callId,
    skip: !!lastUpdatedAtTimestamp && !!lastUpdatedByUserId
  });
  const {
    nextBestActions,
    nextBestActionsLoadingStatus,
    refetchNextBestActions
  } = useNextBestActions({
    callId,
    callSummary,
    initialNextBestActions,
    isUngatedForNextBestActions,
    shouldShowNextBestActions
  });
  useEffect(() => {
    if (callSummaryHistory && !isLoadingCallSummaryHistory && !fetchCallPropertyHistoryError) {
      const updatedByUserId = callSummaryHistory.updatedByUserId || null;
      const timestamp = callSummaryHistory.timestamp || null;
      setLastUpdatedByUserId(updatedByUserId);
      setLastUpdatedAtTimestamp(timestamp);
    }
  }, [callSummaryHistory, isLoadingCallSummaryHistory, fetchCallPropertyHistoryError]);
  const skipPolling = !executionId || executionId === initialCallSummaryExecutionId;
  const {
    callSummaryFromPolling,
    isPollForCallSummaryLoading,
    pollForCallSummaryError,
    stopPolling
  } = usePollForCallSummary(executionId, skipPolling);
  useEffect(() => {
    if (pollForCallSummaryError) {
      stopPolling();
      setShowError(true);
      resetLoadingState();
      logNetworkError({
        error: new Error('Error fetching call summary'),
        networkError: pollForCallSummaryError
      });
      return;
    }
    const pollingForCallSummaryCompleted = callSummaryFromPolling && !isPollForCallSummaryLoading && !isLoadingUserInfo;
    if (pollingForCallSummaryCompleted) {
      stopPolling();
      setCallSummary(callSummaryFromPolling);
      setLastUpdatedByUserId(userInfo && userInfo.user_id || null);
      setLastUpdatedAtTimestamp(new Date().toISOString());
      resetLoadingState();
      if (handleEngagementDataUpdated) {
        handleEngagementDataUpdated({
          engagementId
        });
      }
    }
  }, [callSummaryFromPolling, isPollForCallSummaryLoading, pollForCallSummaryError, stopPolling, setCallSummary, userInfo, isLoadingUserInfo, timeoutId, handleEngagementDataUpdated, engagementId]);
  const handleGenerateCallSummary = useCallback(() => {
    if (showInitialLoading || showExtendedLoading) {
      return;
    }
    setShowInitialLoading(true);
    timeoutId.current = setTimeout(() => {
      setShowInitialLoading(false);
      setShowExtendedLoading(true);
    }, initialLoadingTimeout);
    generateCallSummary(callId).then(res => {
      if (res.error) {
        throw res.error;
      }
      const _executionId = res.result.executionId;
      setExecutionId(_executionId);
      refetchNextBestActions();
    }).catch(networkError => {
      resetLoadingState();
      setShowError(true);
      logNetworkError({
        networkError,
        error: new Error('Generate call summary request failed'),
        tags: {
          requestName: 'calls/v1/call-summaries'
        }
      });
    });
    const action = callSummary ? 'clicked re-generate call summary' : 'clicked generate call summary';
    usageTracker.track('callSummary', {
      action
    });
  }, [callId, callSummary, showExtendedLoading, showInitialLoading, usageTracker, refetchNextBestActions]);
  const handleTrackLink = useCallback(() => {
    usageTracker.track('callSummary', {
      action: 'clicked content assistance settings link'
    });
  }, [usageTracker]);
  const disabledTooltipMessage = !customerAnalysisEnabled ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "call-summary.disabledTooltip.contentAssistanceDisabled.message"
    }), /*#__PURE__*/_jsx("div", {
      className: "p-top-2",
      children: /*#__PURE__*/_jsx(UILink, {
        href: contentAssistanceSettingsURL,
        external: true,
        onClick: handleTrackLink,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "call-summary.disabledTooltip.contentAssistanceDisabled.link"
        })
      })
    })]
  }) : isTranscriptScheduled ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "call-summary.disabledTooltip.transcriptionScheduled"
  }) : isTranscriptEmpty ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "call-summary.disabledTooltip.transcriptionEmpty"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "call-summary.disabledTooltip.cannotEdit"
  });
  if (enviro.isProd() && showInitialLoading) {
    loadCSATSurvey(CALL_SUMMARIZATION_SURVEY_ID);
  }
  return /*#__PURE__*/_jsx(CallSummaryWrapper, {
    style: style,
    children: showError ? /*#__PURE__*/_jsx(UIAlert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "call-summary.generating.error.title"
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "call-summary.generating.error.message"
      })
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(GenerateButton, {
        disabled: !canEdit || !customerAnalysisEnabled || isTranscriptEmpty,
        disabledTooltipMessage: disabledTooltipMessage,
        isLoading: showInitialLoading || showExtendedLoading,
        hasCallSummary: !!callSummary,
        onClick: handleGenerateCallSummary
      }), /*#__PURE__*/_jsx(CallSummaryContentWrapper, {
        style: {
          padding: 0
        },
        children: /*#__PURE__*/_jsx(CallSummaryContent, {
          callSummary: callSummary,
          lastUpdatedAtTimestamp: lastUpdatedAtTimestamp,
          lastUpdatedByUserId: lastUpdatedByUserId,
          isLoadingCallSummaryHistory: isLoadingCallSummaryHistory,
          isExtendedLoadingCallSummary: showExtendedLoading,
          fetchCallSummaryHistoryError: !!fetchCallPropertyHistoryError,
          nextBestActions: nextBestActions,
          isUngatedForNextBestActions: isUngatedForNextBestActions,
          nextBestActionsLoadingStatus: nextBestActionsLoadingStatus,
          refetchNextBestActions: refetchNextBestActions,
          shouldShowNextBestActions: shouldShowNextBestActions
        })
      }), callSummary && /*#__PURE__*/_jsx(Feedback, {
        callId: callId,
        usageTracker: usageTracker
      })]
    })
  });
};
const CallSummaryWithProvider = props => {
  return /*#__PURE__*/_jsx(DataFetchingClientProvider, {
    client: client,
    children: /*#__PURE__*/_jsx(CallSummary, Object.assign({}, props))
  });
};
export default CallSummaryWithProvider;